// @flow
import type { ViewType } from 'catena/er2_map/src/map'
import Map from './map'

type Properties = {
    currentViewData?: ViewType,
    // Extent list for previous navigation.
    navHistoryUndo: any[],
    // Next extent list.
    navHistoryRedo: any[],
    ignoreMoveEnd: boolean,
    onNavigationHistoryChanged: Function[],
}

class MapHistory {
    map: Map
    onNavigationHistoryChanged: Function
    properties: Properties

    constructor(map: Map, onNavigationHistoryChanged: Function) {
        this.map = map
        this.onNavigationHistoryChanged = onNavigationHistoryChanged

        // Properties of the map
        this.properties = {
            navHistoryUndo: [],
            navHistoryRedo: [],
            ignoreMoveEnd: false,
        }
        this.trackHistory()
    }

    /**
     * Update navigation history stacks
     */
    trackHistory() {
        this.map.olmap.on(['moveend'], (e) => {
            const view = this.map.olmap.getView().getProperties()
            const viewData = {
                center: view.center,
                resolution: view.resolution,
                rotation: view.rotation,
            }

            // When navigation history is popped, a moveend event will be triggered. We don't want to modify
            //   the history state when this happens.
            if (this.properties.ignoreMoveEnd) {
                this.properties.ignoreMoveEnd = false

                // Check if the current extent should be initialized
                if (!this.properties.currentViewData) this.properties.currentViewData = viewData
            } else {
                this.historyPush(viewData)
                this.onNavigationHistoryChanged()
            }
        })
    }

    /**
     * Add the extent data to the history stack.
     * @param {object} extentData Keys are center, resolution and rotation
     */
    historyPush(viewData: ViewType) {
        if (!this.properties.currentViewData ||
            viewData.resolution !== this.properties.currentViewData.resolution ||
            viewData.center[0] !== this.properties.currentViewData.center[0] ||
            viewData.center[1] !== this.properties.currentViewData.center[1]
        ) {
            if (this.properties.currentViewData) {
                this.properties.navHistoryUndo.push(this.properties.currentViewData)
            }

            // Preserve the current extent
            this.properties.currentViewData = viewData

            // Reset the redo stack
            this.properties.navHistoryRedo = []
        }
    }

    /**
     * Go to next map extent in history
     */
    historyNext() {
        this.historyGo('navHistoryRedo', 'navHistoryUndo')
    }

    /**
     * Go to previous map extent in history
     */
    historyPrevious() {
        this.historyGo('navHistoryUndo', 'navHistoryRedo')
    }

    /**
     * Push the current extent onto the save stack and set the view to the extent on the source stack.
     * @param {array} sourceStack source of array of extents to pull from
     * @param {array} saveStack source of array of extents to push to
     */
    historyGo(sourceStack: string, saveStack: string) {
        const hist: ViewType[] = this.properties[sourceStack]
        if (hist.length) {
            const last = hist.pop()

            // Current view data gets saved
            this.properties[saveStack].push(this.properties.currentViewData)

            // Update current view with last extent on history stack
            this.properties.currentViewData = last
            this.properties.ignoreMoveEnd = true

            this.map.zoomToCenterResolutionRotation(last)
            this.onNavigationHistoryChanged()
        }
    }

    hasHistoryPrev() {
        return this.properties.navHistoryUndo.length > 0
    }

    hasHistoryNext() {
        return this.properties.navHistoryRedo.length > 0
    }
}

export default MapHistory
